// src/pages/CreateRecordPage.tsx
import React, { useEffect, useContext, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Checkbox,
    Box,
    TextField,
    Button,
    Typography,
    Stack,
    IconButton,
    useMediaQuery,
    useTheme,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Chip,
    Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    saveRecord,
    getRecord,
    uploadAttachments,
    deleteAttachment,
    saveDraft,
} from '../api/records';
import { fetchAllTags, fetchMetadata, updateMetadata } from '../api/metadata';
import { AuthContext } from '../context/AuthContext';
import templatesJson from '../data/templates.json';
import HashReferenceAutocomplete, {
    invalidateHashReferenceCache,
} from '../components/HashReferenceAutocomplete';

type LocationState = {
    returnPath?: string;
    title?: string;
    content?: string;
    recordTime?: string;
    revisionOf?: string;
    revisionGroup?: string;
};

type Template = {
    template_name: string;
    boilerplate_heading: string;
    boilerplate_text: string;
};

type ApiAttachment = { id: string };

type DraftRecordApi = {
    id: string; // record UUID
    triplet_id: string;
    title: string;
    content: string;
    record_time: string; // ISO string (maybe without trailing Z)
    attachments: ApiAttachment[];
    draft: boolean;
    revision_group?: string | null;
};

type SaveDraftApiResponse = {
    id: string;
    triplet_id: string;
    revision_group?: string | null;
};

const templates = templatesJson as Template[];

const UTCToLocal = (date: Date): string => {
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

function haveSameContents(a: string[], b: string[]) {
    if (a.length !== b.length) return false;
    const A = [...a].sort();
    const B = [...b].sort();
    return A.every((val, i) => val === B[i]);
}

const CreateRecordPage: React.FC = () => {
    const { triplet_id } = useParams<{ triplet_id: string }>();
    const [recordUUID, setRecordUUID] = useState<string>(crypto.randomUUID());

    const location = useLocation();
    const state = (location.state as LocationState) || {};

    const [error, setError] = useState<string>();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { email, isLoggedIn } = useContext(AuthContext);

    const navigate = useNavigate();

    const [selectedTemplate, setSelectedTemplate] = useState<string>('');

    const defaultRecordTime = new Date();

    const [title, setTitle] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [recordTime, setRecordTime] = useState<Date>(defaultRecordTime);
    const [attachmentUUIDs, setAttachmentUUIDs] = useState<string[]>([]);
    const [includeDate, setIncludeDate] = useState<boolean>(false);

    // snapshot of last-saved editor fields
    const [savedTitle, setSavedTitle] = useState<string>('');
    const [savedContent, setSavedContent] = useState<string>('');
    const [savedRecordTime, setSavedRecordTime] =
        useState<Date>(defaultRecordTime);
    const [savedAttachmentUUIDs, setSavedAttachmentUUIDs] = useState<string[]>(
        []
    );

    const [savingPermanentRecord, setSavingPermanentRecord] =
        useState<boolean>(false);
    const [savingDraft, setSavingDraft] = useState<boolean>(false);
    const [savingAttachments, setSavingAttachments] = useState<boolean>(false);

    // prevent duplicate submits across navigation/rerenders
    const submittedOnceRef = useRef<boolean>(false);

    // --- Tags (local-only until Save) ---
    const [localTags, setLocalTags] = useState<string[]>([]);
    const [savedTags, setSavedTags] = useState<string[]>([]);
    const [allTags, setAllTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState<string>('');
    const [revisionGroupId, setRevisionGroupId] = useState<string | null>(
        state?.revisionGroup ?? null
    );

    // Load global tag suggestions once
    useEffect(() => {
        (async () => {
            try {
                const tags = await fetchAllTags();
                setAllTags(tags || []);
            } catch (e) {
                console.error('Failed to fetch global tags', e);
            }
        })();
    }, []);

    // If editing an existing draft, seed fields + tags
    useEffect(() => {
        if (!triplet_id) return;
        (async () => {
            try {
                const data = (await getRecord(triplet_id)) as DraftRecordApi;

                if (!data.draft) {
                    navigate(`/records/${data.triplet_id}`);
                    return;
                }

                setRecordUUID(data.id);
                setTitle(data.title);
                setContent(data.content);
                setRecordTime(new Date(data.record_time + 'Z'));
                setAttachmentUUIDs(
                    data.attachments.map((a: ApiAttachment) => a.id)
                );

                setSavedTitle(data.title);
                setSavedContent(data.content);
                setSavedRecordTime(new Date(data.record_time + 'Z'));
                setSavedAttachmentUUIDs(
                    data.attachments.map((a: ApiAttachment) => a.id)
                );

                if (data.revision_group) {
                    setRevisionGroupId(data.revision_group);
                    try {
                        const meta = (await fetchMetadata(
                            data.revision_group
                        )) as { tags?: string[] };
                        const tags = meta?.tags ?? [];
                        setLocalTags(tags);
                        setSavedTags(tags);
                    } catch (e) {
                        console.error('Failed to fetch metadata for tags', e);
                    }
                }
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                console.error('API error:', e);
                setError(msg || 'Unknown error');
            }
        })();
    }, [triplet_id, navigate]);

    const tagDirty = !haveSameContents(localTags, savedTags);

    const isDirty =
        title !== savedTitle ||
        content !== savedContent ||
        !haveSameContents(attachmentUUIDs, savedAttachmentUUIDs) ||
        ((!!includeDate || !!triplet_id) &&
            recordTime.getTime() !== savedRecordTime.getTime()) ||
        tagDirty;

    // Persist tags given a known revision group id
    const persistTags = async (rgid: string) => {
        try {
            await updateMetadata(rgid, { tags: localTags });
            setSavedTags(localTags);
        } catch (e) {
            console.error('Failed to update tags metadata', e);
        }
    };

    // Save or update draft (NO tag push here)
    const saveCurrentDraft = async (overrideAttachments?: string[]) => {
        setSavingDraft(true);
        const attachmentsForSave = overrideAttachments ?? attachmentUUIDs;

        const record: {
            recordUUID: string;
            title: string;
            content: string;
            attachment_uuids?: string[];
            record_time: string;
            revision_of?: string;
            revision_group?: string;
        } = {
            recordUUID,
            title: title.trim(),
            content: content.trim(),
            record_time: recordTime.toISOString(),
            attachment_uuids: attachmentsForSave,
            ...(state?.revisionOf ? { revision_of: state?.revisionOf } : {}),
            ...(state?.revisionGroup
                ? { revision_group: state?.revisionGroup }
                : {}),
        };

        const record_entry = (await saveDraft(record)) as SaveDraftApiResponse;

        // Save snapshot
        setSavedTitle(title);
        setSavedContent(content);
        setSavedRecordTime(recordTime);
        setSavedAttachmentUUIDs(attachmentsForSave);

        // Capture revision group if present
        if (record_entry?.revision_group && !revisionGroupId) {
            setRevisionGroupId(record_entry.revision_group);
        }

        if (triplet_id !== record_entry.triplet_id) {
            navigate('/record-editor/' + record_entry.triplet_id);
        }

        setSavingDraft(false);
        return record_entry;
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files;
        if (!files) return;

        setSavingAttachments(true);
        try {
            const uploads = (await uploadAttachments(
                recordUUID,
                Array.from(files)
            )) as Array<{ id: string }>;
            const added = uploads.map((u) => u.id);
            const next = [...attachmentUUIDs, ...added];
            setAttachmentUUIDs(next);

            // Auto-create a draft immediately on a brand new record
            if (!triplet_id) {
                await saveCurrentDraft(next);
            }
        } finally {
            setSavingAttachments(false);
        }
    };

    const handleRemoveFile = (index: number) => {
        deleteAttachment(attachmentUUIDs[index]);
        setAttachmentUUIDs((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selectedName = event.target.value;
        setSelectedTemplate(selectedName);

        if (selectedName === '') {
            setTitle('');
            setContent('');
            return;
        }

        const selected = templates.find(
            (t) => t.template_name === selectedName
        );
        if (selected) {
            setTitle(selected.boilerplate_heading || '');
            setContent(selected.boilerplate_text || '');
        }
    };

    const handleSaveDraft = async (event: React.FormEvent) => {
        event.preventDefault();
        const entry = await saveCurrentDraft();

        // If tags changed, push them now with a known revision_group
        if (tagDirty) {
            let rg = revisionGroupId || entry?.revision_group || null;
            if (!rg) {
                const fresh = (await getRecord(
                    entry.triplet_id
                )) as DraftRecordApi;
                rg = fresh.revision_group ?? null;
                if (rg) setRevisionGroupId(rg);
            }
            if (rg) await persistTags(rg);
        }
    };

    const handleSaveRecord = async (event: React.FormEvent) => {
        event.preventDefault();

        if (submittedOnceRef.current || savingPermanentRecord) return;
        submittedOnceRef.current = true;
        setSavingPermanentRecord(true);

        try {
            if (!isLoggedIn()) {
                throw new Error('Unauthorized: Please log in again');
            }
            if (!email) {
                throw new Error('User email is required');
            }
            if (!title.trim() || !content.trim()) {
                alert(
                    'You must fill out both title and content fields before submitting a permanent record'
                );
                return;
            }

            // Save latest draft (this does NOT push tags)
            const entry = isDirty ? await saveCurrentDraft() : null;

            // Push tags if they changed (resolve revision_group once, with safe triplet fallback)
            if (tagDirty) {
                let rg = revisionGroupId || entry?.revision_group || null;
                if (!rg) {
                    const freshTriplet: string | undefined =
                        entry?.triplet_id ?? triplet_id;
                    if (freshTriplet) {
                        const fresh = (await getRecord(
                            freshTriplet
                        )) as DraftRecordApi;
                        rg = fresh.revision_group ?? null;
                        if (rg) setRevisionGroupId(rg);
                    }
                }
                if (rg) await persistTags(rg);
            }

            // Finalize the record; swallow 404 if already finalized by a stray duplicate call
            try {
                await saveRecord(recordUUID);
            } catch (e) {
                const status =
                    (e as { response?: { status?: number }; status?: number })
                        ?.response?.status ?? (e as { status?: number }).status;
                if (status !== 404) throw e;
            }

            invalidateHashReferenceCache();

            const targetTriplet = triplet_id || entry?.triplet_id;
            if (targetTriplet) {
                navigate(`/records/${targetTriplet}`);
            }
        } catch (err) {
            console.error('Error creating record:', err);
            alert('Failed to create record. Please try again.');
            submittedOnceRef.current = false;
        } finally {
            setSavingPermanentRecord(false);
        }
    };

    // --- Tag editor helpers (local-only updates) ---
    const handleAddTag = (newTag: string) => {
        const cleaned = newTag.trim();
        if (!cleaned) {
            setTagInput('');
            return;
        }
        if (localTags.includes(cleaned)) {
            setTagInput('');
            return;
        }
        setLocalTags((prev) => [...prev, cleaned]);
        setAllTags((prev) => Array.from(new Set([...prev, cleaned])));
        setTagInput('');
    };

    const handleDeleteTag = (tagToRemove: string) => {
        setLocalTags((prev) => prev.filter((t) => t !== tagToRemove));
    };

    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mt: 2,
                        width: { xs: '100%', sm: '80%' },
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        Record Editor
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mt: 2,
                        width: { xs: '100%', sm: '20%' },
                    }}
                >
                    <FormControl fullWidth variant="outlined" margin="normal">
                        <InputLabel id="template-select-label">
                            Select Template
                        </InputLabel>
                        <Select
                            labelId="template-select-label"
                            value={selectedTemplate}
                            onChange={handleChange}
                            label="Select Template"
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {templates.map(
                                (template: Template, idx: number) => (
                                    <MenuItem
                                        key={idx}
                                        value={template.template_name}
                                    >
                                        {template.template_name}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box component="form" onSubmit={handleSaveRecord} noValidate>
                {/* Title + Save buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mt: 2,
                    }}
                >
                    <TextField
                        label="Title"
                        margin="none"
                        variant="outlined"
                        required
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}
                        tabIndex={1}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={savingPermanentRecord}
                        sx={{
                            flexGrow: 0,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            alignSelf: 'stretch',
                        }}
                        tabIndex={4}
                    >
                        {savingPermanentRecord
                            ? 'Submitting...'
                            : 'Save as permanent record'}
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        disabled={!isDirty || savingDraft}
                        sx={{
                            flexGrow: 0,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            alignSelf: 'stretch',
                        }}
                        tabIndex={4}
                        onClick={handleSaveDraft}
                    >
                        {savingDraft
                            ? 'Saving draft'
                            : isDirty
                              ? 'Save draft'
                              : 'Draft saved'}
                    </Button>
                </Box>

                {/* Tags (local-only until Save) */}
                <Box sx={{ mt: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {localTags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                onDelete={() => handleDeleteTag(tag)}
                                size="small"
                                sx={{
                                    bgcolor: 'grey.300',
                                    color: 'black',
                                    height: 28,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    borderRadius: 1.5,
                                }}
                            />
                        ))}

                        <Autocomplete
                            freeSolo
                            options={allTags.filter(
                                (t) => !localTags.includes(t)
                            )}
                            inputValue={tagInput}
                            onInputChange={(_, value) => setTagInput(value)}
                            onChange={(_, value, reason) => {
                                if (
                                    reason === 'selectOption' &&
                                    typeof value === 'string'
                                ) {
                                    handleAddTag(value);
                                }
                            }}
                            value={null}
                            sx={{ minWidth: 140 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Add tag"
                                    size="small"
                                    sx={{
                                        height: 28,
                                        fontSize: '0.75rem',
                                        '& .MuiOutlinedInput-root': {
                                            height: 28,
                                            px: 1,
                                            borderRadius: 1.5,
                                            fontSize: '0.75rem',
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 0.5,
                                            px: 1,
                                        },
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault(); // don't submit the outer form
                                            if (tagInput.trim())
                                                handleAddTag(tagInput);
                                        }
                                    }}
                                />
                            )}
                            componentsProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: { offset: [0, 4] },
                                        },
                                    ],
                                    sx: {
                                        '& .MuiAutocomplete-listbox': {
                                            fontSize: '0.75rem',
                                            py: 0.5,
                                        },
                                        '& .MuiAutocomplete-option': {
                                            minHeight: '32px',
                                            padding: '4px 8px',
                                        },
                                    },
                                },
                            }}
                        />
                    </Stack>
                </Box>

                {/* Editor, attachments, record time */}
                <Stack spacing={2}>
                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={2}
                        sx={{ width: '100%' }}
                    />

                    <Stack>
                        <Box sx={{ width: '100%' }}>
                            <HashReferenceAutocomplete
                                value={content}
                                onChange={setContent}
                                textField={
                                    <TextField
                                        label="Content (Markdown)"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        multiline
                                        rows={10}
                                        required
                                        value={content}
                                        tabIndex={2}
                                    />
                                }
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        width: { xs: '100%', sm: '30%' },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        component="label"
                                        sx={{ mt: 2 }}
                                        tabIndex={3}
                                        disabled={savingAttachments}
                                    >
                                        {savingAttachments
                                            ? 'Uploading Attachments...'
                                            : 'Upload Attachments'}
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            onChange={handleFileChange}
                                            tabIndex={-1}
                                        />
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        width: { xs: '100%', sm: '65%' },
                                    }}
                                >
                                    <FormControl sx={{ mt: 2 }}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                        >
                                            {!triplet_id && (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                includeDate
                                                            }
                                                            onChange={(e) =>
                                                                setIncludeDate(
                                                                    e.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Include custom record time"
                                                />
                                            )}
                                            {(includeDate || triplet_id) && (
                                                <TextField
                                                    label="Record Time"
                                                    type="datetime-local"
                                                    value={UTCToLocal(
                                                        recordTime
                                                    )}
                                                    onChange={(e) =>
                                                        setRecordTime(
                                                            new Date(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            )}
                                        </Stack>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {attachmentUUIDs.map((attachment, index) => (
                                <Box
                                    key={attachment}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`/api/records/attachment/${attachment}?draft=true`}
                                        alt={attachment}
                                        sx={{
                                            maxWidth: 200,
                                            maxHeight: 200,
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                            boxShadow: 1,
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <IconButton
                                        sx={{
                                            height: 'min-content',
                                            margin: 'auto',
                                        }}
                                        aria-label="delete"
                                        onClick={() => handleRemoveFile(index)}
                                    >
                                        <DeleteIcon color="primary" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Stack>

                    {/* Preview */}
                    <Stack>
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                minHeight: 250,
                                overflow: 'auto',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Preview
                            </Typography>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {content || 'Nothing to preview yet.'}
                            </ReactMarkdown>
                        </Box>
                    </Stack>
                </Stack>
            </Box>
        </Container>
    );
};

export default CreateRecordPage;
